import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import ThemeProvider from './theme/ThemeProvider';
import React, { lazy, Suspense, useContext, useEffect, useState } from 'react';
import { UserContext } from './contexts/userContext';
import Login from './components/login';
import { IntlProvider } from 'react-intl';
import en from './lang/en.json';
import de from './lang/de.json';
import fr from './lang/fr.json';
import it from './lang/it.json';
import SuspenseLoader from './components/SuspenseLoader';
import axiosConfig, { axiosConfigWithoutToken } from './Resources/axiosConfig';
import SidebarLayout from './layouts/SidebarLayout';
import { TokenContext } from './contexts/tokenContext';
import InstallationTabs from './content/dashboards/Installations/index';
import routes from 'src/Resources/routes.json';
import './App.css';
import ForgotPassword from './components/ForgotPassword';
import InstallationsContextProvider from './contexts/InstallationsContextProvider';
import AccessContextProvider from './contexts/AccessContextProvider';
import SalidomoInstallationTabs from './content/dashboards/SalidomoInstallations';
import SodioHomeInstallationTabs from './content/dashboards/SodiohomeInstallations';
import { ProductIdContext } from './contexts/ProductIdContextProvider';
import { TourProvider } from './contexts/TourContext';
import Tickets from './content/dashboards/Tickets';

function App() {
  const context = useContext(UserContext);
  const { currentUser, setUser } = context;
  const tokencontext = useContext(TokenContext);
  const { token, setNewToken } = tokencontext;
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const username = searchParams.get('username');
  const {
    accessToSalimax,
    accessToSodiohome,
    accessToSodistore,
    accessToSodistoreGrid,
    accessToSodistorePro,
    setAccessToSalimax,
    setAccessToSalidomo,
    setAccessToSodiohome,
    setAccessToSodistore,
    setAccessToSodistoreGrid,
    setAccessToSodistorePro
  } = useContext(ProductIdContext);

  const defaultRoute = accessToSodiohome
    ? routes.sodiohome_installations
    : accessToSodistorePro
    ? routes.sodistorepro_installations
    : accessToSodistoreGrid
    ? routes.sodistoregrid_installations
    : accessToSodistore
    ? routes.sodistore_installations
    : accessToSalimax
    ? routes.installations
    : routes.salidomo_installations;

  const detectBrowserLanguage = (): string => {
    const browserLang = navigator.language?.toLowerCase() || '';
    if (browserLang.startsWith('de')) return 'de';
    if (browserLang.startsWith('fr')) return 'fr';
    if (browserLang.startsWith('it')) return 'it';
    return 'en';
  };

  const [language, setLanguage] = useState<string>(
    () => localStorage.getItem('language') || currentUser?.language || detectBrowserLanguage()
  );

  const onSelectLanguage = (lang: string) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
    if (currentUser) {
      const updatedUser = { ...currentUser, language: lang };
      setUser(updatedUser);
      axiosConfig.put('/UpdateUser', updatedUser).catch(() => {});
    }
  };

  // Sync localStorage language to DB when it differs (e.g. user changed language before new code was deployed)
  useEffect(() => {
    if (currentUser && token) {
      const storedLang = localStorage.getItem('language');
      if (storedLang && storedLang !== currentUser.language) {
        const updatedUser = { ...currentUser, language: storedLang };
        setUser(updatedUser);
        axiosConfig.put('/UpdateUser', updatedUser).catch(() => {});
      }
    }
  }, [token]);

  const getTranslations = () => {
    switch (language) {
      case 'de': return de;
      case 'fr': return fr;
      case 'it': return it;
      default:   return en;
    }
  };

  const Loader = (Component) => (props) =>
    (
      <Suspense fallback={<SuspenseLoader />}>
        <Component {...props} />
      </Suspense>
    );

  const ResetPassword = Loader(
    lazy(() => import('src/components/ResetPassword'))
  );

  const SetNewPassword = Loader(
    lazy(() => import('src/components/SetNewPassword'))
  );

  const Login = Loader(lazy(() => import('src/components/login')));
  const Users = Loader(lazy(() => import('src/content/dashboards/Users')));
  const Report = Loader(lazy(() => import('src/content/dashboards/Report')));

  useEffect(() => {
    if (!username || token) return;

    axiosConfigWithoutToken
      .post('/Login', null, { params: { username, password: '' } })
      .then((response) => {
        if (response.data && response.data.token) {
          // Clear the username param from URL to prevent re-login loops
          const url = new URL(window.location.href);
          url.searchParams.delete('username');
          url.searchParams.delete('reset');
          window.history.replaceState({}, '', url.pathname);

          setNewToken(response.data.token);
          setUser(response.data.user);
          setAccessToSalimax(response.data.accessToSalimax);
          setAccessToSalidomo(response.data.accessToSalidomo);
          setAccessToSodiohome(response.data.accessToSodioHome);
          setAccessToSodistore(response.data.accessToSodistoreMax);
          setAccessToSodistoreGrid(response.data.accessToSodistoreGrid);
          setAccessToSodistorePro(response.data.accessToSodistorePro);
        }
      })
      .catch(() => {});
  }, [username]);

  if (!token) {
    return (
      <ThemeProvider>
        <IntlProvider
          messages={getTranslations()}
          locale={language}
          defaultLocale="en"
        >
          <CssBaseline />
          <Routes>
            <Route
              path={''}
              element={<Navigate to={routes.login}></Navigate>}
            ></Route>
            <Route path={routes.login} element={<Login></Login>}></Route>
            <Route
              path={routes.forgotPassword}
              element={<ForgotPassword />}
            ></Route>
            <Route
              path={'*'}
              element={<Navigate to={routes.login}></Navigate>}
            ></Route>
          </Routes>
        </IntlProvider>
      </ThemeProvider>
    );
  }

  if (token && currentUser?.mustResetPassword) {
    return (
      <ThemeProvider>
        <IntlProvider
          messages={getTranslations()}
          locale={language}
          defaultLocale="en"
        >
          <CssBaseline />
          <SetNewPassword></SetNewPassword>
        </IntlProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <IntlProvider
        messages={getTranslations()}
        locale={language}
        defaultLocale="en"
      >
        <TourProvider>
        <InstallationsContextProvider>
          <CssBaseline />
          <Routes>
            <Route
              path={''}
              element={<Navigate to={defaultRoute}></Navigate>}
            ></Route>
            <Route
              path={'login'}
              element={<Navigate to={defaultRoute}></Navigate>}
            ></Route>
            <Route
              path="/"
              element={
                <SidebarLayout
                  language={language}
                  onSelectLanguage={onSelectLanguage}
                />
              }
            >
              <Route
                path={routes.installations + '*'}
                element={
                  <AccessContextProvider>
                    <InstallationTabs product={0} />
                  </AccessContextProvider>
                }
              />

              <Route
                path={routes.sodistore_installations + '*'}
                element={
                  <AccessContextProvider>
                    <InstallationTabs product={3} />
                  </AccessContextProvider>
                }
              />

              <Route
                path={routes.salidomo_installations + '*'}
                element={
                  <AccessContextProvider>
                    <SalidomoInstallationTabs product={1} />
                  </AccessContextProvider>
                }
              />

              <Route
                path={routes.sodiohome_installations + '*'}
                element={
                  <AccessContextProvider>
                    <SodioHomeInstallationTabs product={2} />
                  </AccessContextProvider>
                }
              />

              <Route
                path={routes.sodistorepro_installations + '*'}
                element={
                  <AccessContextProvider>
                    <SodioHomeInstallationTabs product={5} />
                  </AccessContextProvider>
                }
              />

              <Route
                path={routes.sodistoregrid_installations + '*'}
                element={
                  <AccessContextProvider>
                    <InstallationTabs product={4} />
                  </AccessContextProvider>
                }
              />

              <Route path={routes.tickets + '*'} element={<Tickets />} />
              <Route path={routes.alarmReport + '*'} element={<Report />} />
              <Route path={routes.users + '*'} element={<Users />} />
              <Route
                path={'*'}
                element={<Navigate to={defaultRoute}></Navigate>}
              ></Route>
              <Route path="ResetPassword" element={<ResetPassword />}></Route>
            </Route>
          </Routes>
        </InstallationsContextProvider>
        </TourProvider>
      </IntlProvider>
    </ThemeProvider>
  );
}

export default App;
